import { fmtNum } from '../utils/fmt';
import StatusDot from './home/StatusDot';
import { moduleStatus, CoinGeckoMark } from './home/HomeModule';
import { fmtClock } from '../utils/fmt';
import { openLanding, openEgyetem } from '../utils/landing';
import { useState, useMemo, useRef, useEffect } from 'react';
import { fmtPrice } from '../utils/format';
import './Watchlist.css';
import { TAG_LESSONS } from '../utils/leckeszam';

const MAX_WATCHLIST = 20;

// ── Helpers ───────────────────────────────────────────────────────────────────

// ── Sparkline (40×20 SVG) ─────────────────────────────────────────────────────

function Sparkline({ data, isUp }) {
  const points = useMemo(() => {
    if (!data || data.length < 2) return null;
    const step    = Math.max(1, Math.floor(data.length / 30));
    const sampled = data.filter((_, i) => i % step === 0);
    if (sampled.length < 2) return null;
    const min   = Math.min(...sampled);
    const max   = Math.max(...sampled);
    const range = max - min || 1;
    const W = 40, H = 20;
    return sampled
      .map((v, i) => {
        const x = (i / (sampled.length - 1)) * W;
        const y = H - 1 - ((v - min) / range) * (H - 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }, [data]);

  if (!points) return <span className="wl-spark-empty" />;
  return (
    <svg width="40" height="20" className="wl-sparkline">
      <polyline
        points={points}
        fill="none"
        stroke={isUp ? 'var(--positive)' : 'var(--negative)'}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Coin icon with letter fallback ────────────────────────────────────────────

function CoinIcon({ src, symbol, size = 20 }) {
  const [err, setErr] = useState(false);
  if (err || !src) {
    return (
      <div
        className="wl-icon-fb"
        style={{ width: size, height: size, fontSize: Math.round(size * 0.44) }}
      >
        {(symbol?.[0] ?? '?').toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={symbol}
      className="wl-icon"
      style={{ width: size, height: size }}
      onError={() => setErr(true)}
    />
  );
}


// ── Add Coin Modal ────────────────────────────────────────────────────────────

function AddCoinModal({ coins, watchlistIds, onAdd, onClose }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Close on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return coins;
    return coins.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.symbol.toLowerCase().includes(q)
    );
  }, [coins, query]);

  const isFull = watchlistIds.length >= MAX_WATCHLIST;

  return (
    <>
      <div className="wl-overlay" onClick={onClose} />
      <div className="wl-modal">
        <div className="wl-modal-hdr">
          <span className="wl-modal-title">HOZZÁADÁS</span>
          <span className="wl-modal-cap">{watchlistIds.length}/{MAX_WATCHLIST}</span>
          <button className="wl-modal-x" onClick={onClose}>×</button>
        </div>

        <input
          ref={inputRef}
          className="wl-modal-search"
          placeholder="Keresés név vagy szimbólum alapján…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />

        {isFull && (
          <div className="wl-modal-full">
            A figyelőlista megtelt (max. {MAX_WATCHLIST}) — előbb távolíts el egy coint.
          </div>
        )}

        <div className="wl-modal-list">
          {filtered.map(coin => {
            const added = watchlistIds.includes(coin.id);
            return (
              <div
                key={coin.id}
                className={`wl-modal-row ${added ? 'added' : ''} ${isFull && !added ? 'disabled' : ''}`}
                onClick={() => { if (!added && !isFull) onAdd(coin.id, coin.name); }}
              >
                <CoinIcon src={coin.image} symbol={coin.symbol} size={18} />
                <div className="wl-modal-info">
                  <span className="wl-modal-sym">{coin.symbol.toUpperCase()}</span>
                  <span className="wl-modal-name">{coin.name}</span>
                </div>
                <span className="wl-modal-price">{fmtPrice(coin.current_price)}</span>
                {added
                  ? <span className="wl-modal-check">✓</span>
                  : <span className="wl-modal-rank">#{coin.market_cap_rank}</span>
                }
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: '16px 12px', fontSize: 11, color: 'var(--text3)', textAlign: 'center' }}>
              Nincs találat erre: „{query}”
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main Watchlist Component ──────────────────────────────────────────────────

export default function Watchlist({
  coins,
  selectedId,
  onSelect,
  watchlistIds,
  onAddToWatchlist,
  onRemoveFromWatchlist,
  style,
  isMobile,
  isOpen,
  onClose,
  showAddHint,
  onAddHintDismiss,
  meta,
  expectedMs,
  user,
  onTagClick,
  tagPending,
}) {
  const [showModal, setShowModal] = useState(false);

  const watchlistCoins = useMemo(
    () => watchlistIds.map(id => coins.find(c => c.id === id)).filter(Boolean),
    [watchlistIds, coins]
  );

  const wlStatus = moduleStatus({ at: meta?.at, expectedMs, stale: meta?.stale });

  const asideClass = [
    'watchlist',
    isMobile && 'watchlist--mobile',
    isMobile && isOpen && 'watchlist--open',
  ].filter(Boolean).join(' ');

  return (
    <aside className={asideClass} style={!isMobile ? style : undefined}>

      {/* Mobile: the header's "Dashboard →" pill is hidden, so the drawer is
          the way back into the ecosystem. Both links keep the session: the
          tokens travel in the #w3k_handoff hash that the landing and the
          egyetem both read (utils/landing.js). */}
      {isMobile && (
        <nav className="wl-mobile-nav" aria-label="web3kurzus oldalak">
          <a
            href="https://web3kurzus.hu/dashboard"
            onClick={e => { e.preventDefault(); openLanding('/dashboard', { user }); }}
          >
            Dashboard →
          </a>
          <a
            href="https://egyetem.web3kurzus.hu/tanfolyamok"
            onClick={e => { e.preventDefault(); openEgyetem('/tanfolyamok', { user }); }}
          >
            Egyetem →
          </a>
          {onTagClick && (
            <button type="button" className="wl-mobile-nav-tag" onClick={onTagClick}>
              {tagPending ? 'TAG: feldolgozás alatt' : `TAG · ${TAG_LESSONS} lecke ingyen →`}
            </button>
          )}
        </nav>
      )}

      {/* Watchlist header */}
      <div className="wl-hdr">
        <span className="wl-section-lbl">FIGYELŐLISTA</span>
        <StatusDot
          status={wlStatus}
          label={wlStatus === 'error' ? 'Az adatforrás most nem elérhető (utolsó ismert árak)' : wlStatus === 'delayed' ? 'Késleltetett: a vártnál régebbi adat' : 'Friss adat'}
          lines={['Forrás: CoinGecko', meta?.at && `Frissítve ${fmtClock(meta.at)}`]}
        />
        {wlStatus === 'error' && <span className="hm-late">nem elérhető</span>}
        {wlStatus === 'delayed' && <span className="hm-late">késleltetett</span>}
        <span className="wl-hdr-count">{watchlistIds.length}/{MAX_WATCHLIST}</span>
        <div className="wl-add-wrap">
          <button
            className="wl-add-btn"
            onClick={() => { setShowModal(true); onAddHintDismiss?.(); }}
            title="Coin hozzáadása" aria-label="Coin hozzáadása"
          >+</button>
          {showAddHint && (
            <div className="wl-add-hint">
              👆 Adj hozzá coinokat a figyelőlistádhoz
            </div>
          )}
        </div>
      </div>

      {/* Coin rows */}
      <div className="watchlist-scroll">
        {watchlistCoins.length === 0 && (
          <div className="wl-empty">Kattints a + gombra, és adj coinokat a figyelőlistádhoz.</div>
        )}
        {watchlistCoins.map(coin => {
          const change  = coin.price_change_percentage_24h;
          const isPos   = (change ?? 0) >= 0;
          return (
            <div
              key={coin.id}
              className={`wl-row ${selectedId === coin.id ? 'selected' : ''}`}
              onClick={() => { onSelect(coin); if (isMobile && onClose) onClose(); }}
            >
              <CoinIcon src={coin.image} symbol={coin.symbol} size={20} />

              <div className="wl-info">
                <span className="wl-sym">{coin.symbol.toUpperCase()}</span>
                <span className="wl-rnk">#{coin.market_cap_rank}</span>
              </div>

              <Sparkline data={coin.sparkline_in_7d?.price} isUp={isPos} />

              <div className="wl-right">
                <span className="wl-price num">{fmtPrice(coin.current_price)}</span>
                <span className={`wl-chg ${isPos ? 'pos' : 'neg'}`}>
                  {isPos ? '▲' : '▼'} {fmtNum(Math.abs(change ?? 0), 2)}%
                </span>
              </div>

              <button
                className="wl-rm"
                onClick={e => { e.stopPropagation(); onRemoveFromWatchlist(coin.id); }}
                title="Eltávolítás a figyelőlistáról" aria-label="Eltávolítás a figyelőlistáról"
              >
                ×
              </button>
            </div>
          );
        })}
        <div className="hm-attr-row wl-attr"><CoinGeckoMark /></div>
      </div>

      {/* A „Partnerek (affiliate)” blokk a láblécbe került (3a): PartnersMenu. */}

      {/* Add coin modal */}
      {showModal && (
        <AddCoinModal
          coins={coins}
          watchlistIds={watchlistIds}
          onAdd={(id, name) => { onAddToWatchlist(id, name); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </aside>
  );
}
